import React, { useState } from "react";
import {
  CalendarIcon,
  FlagIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";

interface OKR {
  id: string;
  objective: string;
  keyResults: {
    id: string;
    description: string;
    target: number;
    current: number;
    status: "on-track" | "at-risk" | "blocked" | "completed";
  }[];
}

interface PlanningCycle {
  type: "30-day" | "quarter" | "year" | "3-year";
  current: string;
  startDate: string;
  endDate: string;
  daysRemaining: number;
}

interface PlanningContextContentProps {
  currentCycle: PlanningCycle;
  okrs: OKR[];
  onCycleChange?: (type: PlanningCycle["type"]) => void;
  onReviewClick?: () => void;
  onPlanNextCycle?: () => void;
}

const PlanningContextContent: React.FC<PlanningContextContentProps> = ({
  currentCycle,
  okrs,
  onCycleChange,
  onReviewClick,
  onPlanNextCycle,
}) => {
  const [selectedCycleType, setSelectedCycleType] = useState<
    PlanningCycle["type"]
  >(currentCycle.type);

  const getStatusIcon = (
    status: "on-track" | "at-risk" | "blocked" | "completed",
  ) => {
    switch (status) {
      case "on-track":
      case "completed":
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case "at-risk":
        return <ExclamationCircleIcon className="w-5 h-5 text-yellow-500" />;
      case "blocked":
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 70) return "bg-green-500";
    if (progress >= 40) return "bg-yellow-500";
    return "bg-red-500";
  };

  const cycleTypes = [
    { type: "30-day" as const, label: "Month" },
    { type: "quarter" as const, label: "Quarter" },
    { type: "year" as const, label: "Year" },
    { type: "3-year" as const, label: "3 Year" },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">
          Planning Context
        </h2>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Current Cycle */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <CalendarIcon className="w-5 h-5 text-gray-500" />
              <h3 className="text-sm font-medium text-gray-900">
                Current Cycle
              </h3>
            </div>
            <span className="text-sm font-semibold text-blue-600">
              {currentCycle.current}
            </span>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Days Remaining</span>
              <span className="font-medium text-gray-900">
                {currentCycle.daysRemaining}
              </span>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all"
                style={{
                  width: `${((30 - currentCycle.daysRemaining) / 30) * 100}%`,
                }}
              />
            </div>
          </div>

          {/* Cycle Type Selector */}
          <div className="mt-4 flex space-x-1">
            {cycleTypes.map((cycle) => (
              <button
                key={cycle.type}
                onClick={() => {
                  setSelectedCycleType(cycle.type);
                  onCycleChange?.(cycle.type);
                }}
                className={`flex-1 px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                  selectedCycleType === cycle.type
                    ? "bg-blue-100 text-blue-700"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {cycle.label}
              </button>
            ))}
          </div>
        </div>

        {/* Company OKRs */}
        <div className="px-6 py-4">
          <div className="flex items-center space-x-2 mb-4">
            <FlagIcon className="w-5 h-5 text-gray-500" />
            <h3 className="text-sm font-medium text-gray-900">Company OKRs</h3>
          </div>

          <div className="space-y-4">
            {okrs.length === 0 ? (
              <p className="text-sm text-gray-500 italic">
                No OKRs set for this cycle
              </p>
            ) : (
              okrs.map((okr) => (
                <div key={okr.id} className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-900">
                    {okr.objective}
                  </h4>
                  <div className="space-y-2">
                    {okr.keyResults.map((kr) => {
                      const progress = (kr.current / kr.target) * 100;
                      return (
                        <div key={kr.id} className="space-y-1">
                          <div className="flex items-start space-x-2">
                            {getStatusIcon(kr.status)}
                            <div className="flex-1">
                              <p className="text-xs text-gray-600">
                                {kr.description}
                              </p>
                              <div className="flex items-center justify-between mt-1">
                                <span className="text-xs text-gray-500">
                                  {kr.current}/{kr.target}
                                </span>
                                <span className="text-xs font-medium text-gray-700">
                                  {Math.round(progress)}%
                                </span>
                              </div>
                              <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                                <div
                                  className={`h-1.5 rounded-full transition-all ${getProgressColor(progress)}`}
                                  style={{
                                    width: `${Math.min(progress, 100)}%`,
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Planning Actions */}
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center space-x-2 mb-4">
            <ChartBarIcon className="w-5 h-5 text-gray-500" />
            <h3 className="text-sm font-medium text-gray-900">
              Planning Actions
            </h3>
          </div>

          <div className="space-y-2">
            <button
              onClick={onReviewClick}
              className="w-full text-left px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
            >
              Review Current OKRs
            </button>
            <button
              onClick={onPlanNextCycle}
              className="w-full text-left px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors"
            >
              Plan Next{" "}
              {selectedCycleType === "30-day"
                ? "Month"
                : selectedCycleType === "quarter"
                  ? "Quarter"
                  : selectedCycleType === "year"
                    ? "Year"
                    : "3 Years"}
            </button>
            <button className="w-full text-left px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors">
              Cascade to Positions
            </button>
          </div>
        </div>

        {/* Next Review */}
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Next Review</span>
            <span className="text-sm font-medium text-gray-900">
              Feb 15, 2025
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanningContextContent;
